import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePrevious } from 'react-use';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import { mergeQueries } from '../../../../common/utils/helpers/routes';
import { round } from '../../../../common/utils/helpers/math';
import { useGlMap } from '../../../contexts/context';
import { LOCATION_SEARCH_PARAM_NAME } from '../../../../common/utils/constants/constants';
import { executeWhenMapLoaded } from '../../../utils/helpers/utils';

// 1s is required for safari with bottom tabs in ios 15 to input unfocus after character typing into search input
const SYNC_DEBOUNCE_WAIT_MS = 1000;

export function parseLocation(string) {
  const [zoom, lat, lng] = string.split('/');
  return { zoom: +zoom, lat: +lat, lng: +lng };
}

export function formatLocation(precision, { zoom, lat, lng }) {
  return [zoom, lat, lng].map(round.fp(precision)).join('/');
}

function getLocationFromSearchParams(locationSearch) {
  const params = new URLSearchParams(locationSearch);

  return params.get(LOCATION_SEARCH_PARAM_NAME);
}

export interface LocationSyncControlProps {
  precision?: number;
}

const LocationSyncControl = ({ precision = 4 }: LocationSyncControlProps) => {
  const glMap = useGlMap();
  const history = useHistory();
  const location = useLocation();

  const locationSearchParamValue = useMemo(
    () => getLocationFromSearchParams(location.search),
    [location.search],
  );
  const prevLocationSearchParamValue = usePrevious(locationSearchParamValue);

  const previousMap = usePrevious(glMap);
  // sychronize from location to map
  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (glMap.isMoving()) {
      return;
    }

    if (!locationSearchParamValue) {
      return;
    }

    // skip if location haven't changed and map is not changed
    if (
      glMap === previousMap &&
      locationSearchParamValue === prevLocationSearchParamValue
    ) {
      return;
    }

    const { zoom, lng, lat } = parseLocation(locationSearchParamValue);

    const roundFunction = round.fp(precision);

    if (roundFunction(glMap.getZoom()) !== zoom) {
      glMap.setZoom(zoom);
    }

    if (!isEqual(glMap.getCenter().toArray().map(roundFunction), [lng, lat])) {
      glMap.setCenter([lng, lat]);
    }
  }, [
    glMap,
    previousMap,
    precision,
    locationSearchParamValue,
    prevLocationSearchParamValue,
  ]);

  // synchronize from map to location
  useEffect(() => {
    if (!glMap) {
      return;
    }

    const syncLocationInternalDebounced = debounce((beforeCallUrlSearch) => {
      // don't update location in url if it's already changed since the function call
      if (
        getLocationFromSearchParams(beforeCallUrlSearch) !==
        getLocationFromSearchParams(history.location.search)
      ) {
        return;
      }

      const zoom = glMap.getZoom();
      const [lng, lat] = glMap.getCenter().toArray();
      const newLocationSearchParamValue = formatLocation(precision, {
        zoom,
        lng,
        lat,
      });

      if (
        getLocationFromSearchParams(beforeCallUrlSearch) ===
        newLocationSearchParamValue
      ) {
        return;
      }

      history.replace({
        ...history.location,
        search: mergeQueries(history.location.search, {
          [LOCATION_SEARCH_PARAM_NAME]: newLocationSearchParamValue,
        }),
      });
    }, SYNC_DEBOUNCE_WAIT_MS);

    const syncLocation = () =>
      syncLocationInternalDebounced(history.location.search);

    glMap.on('moveend', syncLocation);
    executeWhenMapLoaded(glMap, syncLocation);

    return () => {
      glMap.off('moveend', syncLocation);
    };
  }, [glMap, history, precision]);

  return null;
};

export default LocationSyncControl;
